import axios from "axios";

/**
 * Centralized HTTP service for API calls
 * 
 * Configuration:
 * - Base URL: Set via GATSBY_API_BASE_URL environment variable
 * - Default:
 * 
 * Usage:
 *   import http from './services/http';
 *   const response = await http.get('/endpoint');
 *   const response = await http.post('/endpoint', data);
 */

// Resolve base URL for API calls and normalize TenderApex host to HTTPS.
const sanitizeBaseURL = (url) => {
  if (!url || typeof url !== "string") return "";
  const trimmed = url.trim().replace(/\/+$/, "");
  if (!trimmed) return "";
  if (/^http:\/\/api\.tenderapex\.com/i.test(trimmed)) {
    return trimmed.replace(/^http:\/\//i, "https://");
  }
  return trimmed;
};

const resolvedBaseURL = sanitizeBaseURL(process.env.GATSBY_API_BASE_URL);

// Validate that we have a valid URL
if (!resolvedBaseURL || !resolvedBaseURL.startsWith('http')) {
  console.warn("[HTTP Service] Invalid API base URL:", resolvedBaseURL || "(relative)");
}

// Create axios instance with base configuration
const http = axios.create({
  baseURL: resolvedBaseURL,
  timeout: 60000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor - runs before every request
http.interceptors.request.use(
  (config) => {
    // Add auth token if available
    // const token = localStorage.getItem('auth_token');
    // if (token) {
    //   config.headers.Authorization = `Bearer ${token}`;
    // }
    
    return config;
  },
  (error) => {
    // Handle request error (e.g., network error before request is sent)
    console.error("[API Request Error]", error);
    return Promise.reject(error);
  }
);

// Response interceptor - runs after every response
http.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Centralized error handling
    if (error.response) {
      // Server responded with error status (4xx, 5xx)
      const { status, data, config } = error.response;
      console.error(`[API Error] ${config?.method?.toUpperCase()} ${config?.url}`, {
        status,
        message: data?.message || error.message,
        data
      });
      
      // Handle specific error cases
      if (status === 401) {
        // Unauthorized - could trigger logout or redirect to login
        // localStorage.removeItem('auth_token');
        // window.location.href = '/login';
      } else if (status === 403) {
        // Forbidden
        console.warn("Access forbidden");
      } else if (status === 404) {
        // Not found
        console.warn("Resource not found");
      } else if (status >= 500) {
        // Server error
        console.error("Server error occurred");
      }
    } else if (error.request) {
      // Request was made but no response received
      console.error("[API Network Error] No response received", {
        url: error.config?.url,
        message: "Network error or server is unreachable"
      });
    } else {
      // Error setting up the request
      console.error("[API Setup Error]", error.message);
    }
    
    return Promise.reject(error);
  }
);

export default http;


